import React, { useEffect, useRef, useState } from 'react'

import '../design/styles/header.css'

import iconUser from '../design/icons/user.svg'
import iconSlogan from '../design/icons/slogan.svg'
import iconLogo from '../design/icons/logo-full-white.png'
import iconSettings from '../design/icons/settings.svg'

import config from '../config.json'
import auth from '../hooks/auth'
import { isWebAuthnSupported } from '../login/webauthn'
import NotificationsBell from './notifications/NotificationsBell'

const faviconMarkSrc = `${import.meta.env.BASE_URL}favicon.svg`

const Header = ({ onLogout, onOpenChangePassword, onOpenPasskeys, onOpenAiAssistant, onOpenSettingsModule, Alert }) => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false)
  const menuRef = useRef(null)
  const settingsMenuRef = useRef(null)

  useEffect(() => {
    if (!menuOpen) return
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  useEffect(() => {
    if (!settingsMenuOpen) return
    const close = (e) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(e.target)) {
        setSettingsMenuOpen(false)
      }
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setSettingsMenuOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
    }
  }, [settingsMenuOpen])

  const settingsMenuItems = []
  if (auth('company.data', 'r')) {
    settingsMenuItems.push({ id: 'company', label: 'Compañía' })
  }
  if (auth('company.users', 'r')) {
    settingsMenuItems.push({ id: 'users', label: 'Usuarios' })
  }
  if (auth('company.currencies', 'r')) {
    settingsMenuItems.push({ id: 'currencies', label: 'Monedas' })
  }
  if (auth('company.pay_info', 'r')) {
    settingsMenuItems.push({ id: 'pay-cards', label: 'Agregar Tarjeta' })
  }
  if (auth('adm.vou', 'r')) {
    settingsMenuItems.push({ id: 'vouchers', label: 'Comprobantes' })
  }
  if (auth('adm.ter', 'r')) {
    settingsMenuItems.push({ id: 'terminal', label: 'Terminal de ventas' })
  }
  settingsMenuItems.push({ id: 'defaults', label: 'Valores predeterminados' })
  settingsMenuItems.push({ id: 'notifications', label: 'Notificaciones' })
  settingsMenuItems.push({ id: 'help', label: 'Ayuda' })
  settingsMenuItems.push({ id: 'ai-assistant', label: 'Asistente IA' })
  if (isWebAuthnSupported()) {
    settingsMenuItems.push({ id: 'passkeys', label: 'Registrar biometria' })
  }

  const toggleMenu = () => {
    setSettingsMenuOpen(false)
    setMenuOpen((o) => !o)
  }

  const toggleSettingsMenu = () => {
    setMenuOpen(false)
    setSettingsMenuOpen((o) => !o)
  }

  const handleSettingsItemClick = (item) => {
    setSettingsMenuOpen(false)
    if (item.id === 'passkeys') {
      if (typeof onOpenPasskeys === 'function') onOpenPasskeys()
      return
    }
    if (item.id === 'ai-assistant') {
      if (typeof onOpenAiAssistant === 'function') onOpenAiAssistant()
      return
    }
    if (typeof onOpenSettingsModule === 'function') {
      onOpenSettingsModule(item.id)
    }
  }

  const handleChangePassword = () => {
    setMenuOpen(false)
    if (typeof onOpenChangePassword === 'function') onOpenChangePassword()
  }

  const handleLogout = () => {
    setMenuOpen(false)
    if (typeof onLogout === 'function') onLogout()
  }

  return (
    <div className="header">
      <div className="logo">
        <img className="logo-reduced-white" src={iconLogo} alt="Logo" width="150" height="35" />
        <img className="logo-mark" src={faviconMarkSrc} alt="Logo" width="32" height="36" />
        <img className="slogan" src={iconSlogan} alt="" width="80" height="30" />
      </div>
      <div className="header-toolbar">
        <div className="header-user-menu" ref={menuRef}>
          <button type="button" className="header-user-trigger" onClick={toggleMenu} aria-expanded={menuOpen} aria-haspopup="true">
            <img className="header-user-avatar" src={iconUser} alt="" />
            <span className="header-user-name">{config.name || 'usuario'}</span>
          </button>
          {menuOpen && (
            <div className="header-user-dropdown" role="menu">
              <button type="button" className="header-menu-item" role="menuitem" onClick={handleChangePassword}>
                Cambiar contraseña
              </button>
              <button type="button" className="header-menu-item" role="menuitem" onClick={handleLogout}>
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
        <div className="header-toolbar-icons">
          <NotificationsBell Alert={Alert} />
          <div className="header-settings-menu" ref={settingsMenuRef}>
            <button
              type="button"
              className="header-icon-btn"
              aria-label="Configuración"
              aria-expanded={settingsMenuOpen}
              aria-haspopup="true"
              onClick={toggleSettingsMenu}
            >
              <img className="header-toolbar-icon" src={iconSettings} alt="" />
            </button>
            {settingsMenuOpen && (
              <div className="header-user-dropdown" role="menu">
                {settingsMenuItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="header-menu-item"
                    role="menuitem"
                    onClick={() => handleSettingsItemClick(item)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Header
